/**
 * Líneas de mercancía Invoice PTC: agrupación por producto, medida y grado.
 * Cada configuración piezas/paquete distinta es una fila de mercancía.
 */
import {
  etiquetaPtbCategoriaEspanolPlural,
  etiquetaPtbFromDescription,
} from './invoicePtbEtiqueta';
import { proformaContractLabel } from './invoiceProformaUtils';
import { isRwNrPackingListRow } from '../packinglist/packingListRwNr';

export {
  etiquetaPtbCategoriaEspanolPlural,
  etiquetaPtbFromDescription,
} from './invoicePtbEtiqueta';

function safe(s) {
  return s == null ? '' : String(s);
}

export function isRwNrPackingRow(row) {
  return isRwNrPackingListRow(row);
}

export function productNameFromPackingRow(row) {
  const t = safe(row?.productos_texto).trim();
  if (t) return t;
  const parts = [row?.grado_celda, row?.proveedor_celda, row?.almacen_celda]
    .map((x) => safe(x).trim())
    .filter(Boolean);
  return parts.join(' / ') || '—';
}

function fmtMeasureInvoice(v) {
  if (v == null || v === '') return '';
  const n = parseFloat(String(v).replace(/,/g, '.').replace(/\s/g, ''));
  if (Number.isFinite(n)) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }
  return safe(v).trim();
}

/** Línea de medidas (espesor x ancho x largo) como en packing list. */
export function dimensionLineFromPackingRow(row) {
  const espRaw = safe(row?.espesor).trim();
  const espPart = espRaw ? (/\bmm\b/i.test(espRaw) ? espRaw : `${espRaw} MM`) : '';
  const txt = `${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`.toUpperCase();
  const lar = row?.largo;
  if (/\bRW\b/.test(txt)) {
    const larFmt = lar != null && lar !== '' ? fmtMeasureInvoice(lar) : '';
    const parts = [espPart, 'RW', larFmt].filter(Boolean);
    return parts.join(' X ').replace(/\s+/g, ' ').trim();
  }
  const w = fmtMeasureInvoice(row?.ancho);
  const l = fmtMeasureInvoice(lar);
  if (!espPart && !w && !l) return '';
  return [espPart, w, l].filter(Boolean).join(' X ').replace(/\s+/g, ' ').trim();
}

function internalCodeFromGroup(g) {
  const nf = safe(g.sampleRow?.nf_celda).trim();
  if (nf) return nf;
  return g.piezasPorPaquete || '';
}

/** Valor mm para columnas Espessura / Largura / Comprimento del PDF PTB. */
export function formatDimMmForPtb(v) {
  const n = parseDimNumber(v);
  if (n == null) return '';
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return String(n);
}

/**
 * Dimensiones PTB desde línea de packing list (espesor, ancho, largo).
 * No usar piezas_por_paquete: ahí va la configuración de paquetes (210, 1080…), no mm.
 */
export function ptbDimsFromPackingRow(row) {
  if (!row) return { espessura: '', largura: '', comprimento: '' };
  const txt = `${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`.toUpperCase();
  const esp = formatDimMmForPtb(row?.espesor);
  const lar = formatDimMmForPtb(row?.largo);
  if (/\bRW\b/.test(txt) || /\bRW\b/i.test(safe(row?.ancho))) {
    return { espessura: esp, largura: 'RW', comprimento: lar };
  }
  return {
    espessura: esp,
    largura: formatDimMmForPtb(row?.ancho),
    comprimento: lar,
  };
}

/** Bloque multilínea para columna MERCHANDISE DESCRIPTION del PDF. */
export function merchandiseDescriptionBlock(g, productDescriptionSnapshot) {
  const head =
    safe(productDescriptionSnapshot).trim() || g.productName || '—';
  const dim = dimensionLineFromPackingRow(g.sampleRow);
  const lines = [head];
  if (dim) lines.push(dim);
  if (g.sumBundles > 0) lines.push(`Bundles ${Math.round(g.sumBundles)}`);
  if (g.piezasPorPaquete && g.grado !== 'RW') {
    lines.push(`Pieces/Bundles ${g.piezasPorPaquete}`);
  }
  if (g.sumPiezas > 0 && !isRwNrPackingRow(g.sampleRow)) {
    lines.push(`Pieces ${Math.round(g.sumPiezas)}`);
  }
  return lines.join('\n');
}

/** Deja la descripción general una sola vez, conservando los detalles de cada línea. */
export function normalizeGeneralMerchandiseDescription(lineas, productDescriptionSnapshot = '') {
  const rows = [...(lineas || [])];
  if (rows.length < 2) return rows;

  const firstField = Object.prototype.hasOwnProperty.call(rows[0], 'descriptionBlock')
    ? 'descriptionBlock'
    : 'description';
  const firstDescription = safe(rows[0]?.[firstField]);
  const generalDescription =
    safe(productDescriptionSnapshot).trim() ||
    safe(rows[0]?.productName).trim() ||
    firstDescription.split(/\r?\n/).map((line) => line.trim()).find(Boolean) ||
    '';
  const generalLines = generalDescription
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const normalize = (value) => safe(value).trim().replace(/\s+/g, ' ').toUpperCase();
  if (!generalLines.length) return rows;
  const generalKey = normalize(generalLines.join(' '));
  const generalWordCount = generalLines[0].split(/\s+/).length;

  let kept = false;
  return rows.map((row) => {
    const field = Object.prototype.hasOwnProperty.call(row, 'descriptionBlock')
      ? 'descriptionBlock'
      : Object.prototype.hasOwnProperty.call(row, 'description')
        ? 'description'
        : null;
    if (!field) return row;

    const lines = safe(row[field]).split(/\r?\n/);
    const normalizedLines = lines.map(normalize);
    const normalizedGeneral = generalLines.map(normalize);
    const nextLines = [];
    let changed = false;
    for (let i = 0; i < lines.length; i += 1) {
      const startsWithGeneral =
        generalLines.length === 1 &&
        normalizedLines[i].startsWith(`${generalKey} `);
      if (startsWithGeneral) {
        if (!kept) {
          nextLines.push(lines[i]);
          kept = true;
        } else {
          changed = true;
          const words = [...lines[i].matchAll(/\S+/g)];
          const lastGeneralWord = words[generalWordCount - 1];
          const prefixEnd = lastGeneralWord
            ? lastGeneralWord.index + lastGeneralWord[0].length
            : 0;
          const remainder = lines[i].slice(prefixEnd).trimStart();
          if (remainder) nextLines.push(remainder);
        }
        continue;
      }
      const matchesGeneral = normalizedGeneral.every(
        (line, offset) => normalizedLines[i + offset] === line
      );
      if (!matchesGeneral) {
        nextLines.push(lines[i]);
        continue;
      }
      if (!kept) {
        nextLines.push(...lines.slice(i, i + generalLines.length));
        kept = true;
      } else {
        changed = true;
      }
      i += generalLines.length - 1;
    }

    if (!changed) return row;
    return { ...row, [field]: nextLines.join('\n') };
  });
}

/**
 * Quita la primera línea del detalle si el "texto arriba" de la mercadería ya la incluye
 * (p. ej. EUCALYPTUS VENEER dentro de BRAZILIAN EUCALYPTUS VENEER).
 */
export function stripProductNameIfCoveredByTextoArriba(lineas, opts = {}) {
  const textoArriba = safe(opts.textoArriba).trim();
  if (!textoArriba) return lineas || [];

  const haystack = textoArriba
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .toUpperCase();

  return (lineas || []).map((row) => {
    const field = Object.prototype.hasOwnProperty.call(row, 'descriptionBlock')
      ? 'descriptionBlock'
      : Object.prototype.hasOwnProperty.call(row, 'description')
        ? 'description'
        : null;
    if (!field) return row;

    const lines = safe(row[field]).split(/\r?\n/);
    if (!lines.length) return row;

    const firstNorm = safe(lines[0]).trim().replace(/\s+/g, ' ').toUpperCase();
    if (!firstNorm || !haystack.includes(firstNorm)) return row;

    const rest = lines.slice(1);
    if (!rest.length) return row;
    return { ...row, [field]: rest.join('\n') };
  });
}

function normPiezas(piezas) {
  return safe(piezas).trim().replace(/\s+/g, ' ').toUpperCase();
}

function normGrado(v) {
  return safe(v).trim().toUpperCase();
}

/** Clave de grado solo para cruzar PL ↔ PI: "C+/C" ≡ "C+C". */
function gradoKey(v) {
  return normGrado(v).replace(/[\s/]+/g, '');
}

function calidadLabel(calidadId, calidades) {
  if (calidadId == null || calidadId === '') return '';
  const c = (calidades || []).find((x) => String(x.id_calidad) === String(calidadId));
  if (c?.calidad) return String(c.calidad).trim();
  const raw = String(calidadId).trim();
  if (raw && !/^\d+$/.test(raw)) return raw;
  return '';
}

/** Grado de la línea PL (grado_celda o detección RW en medidas/texto). */
export function gradoFromPackingRow(row) {
  const g = normGrado(row?.grado_celda);
  if (g) return g;
  if (isRwNrPackingRow(row) || isRwPackingRow(row)) return 'RW';
  return '';
}

/** Grado para cruce de precios con la PI: NR se busca como RW. */
export function gradoForPiPricing(row) {
  const g = gradoFromPackingRow(row);
  return g === 'NR' ? 'RW' : g;
}

/** NR y RW comparten grupo comercial y se presentan bajo RW en Facturación. */
export function gradoConsolidadoFromPackingRow(row) {
  const grado = gradoFromPackingRow(row) || normGrado(row?.grado);
  return grado === 'NR' ? 'RW' : grado;
}

/**
 * Piezas/paquete para mercancía invoice: RW/NR vacío (no el valor del Excel logístico).
 * En PDF PTC/PTB no se repite en el bloque descripción (ver merchandiseDescriptionBlock).
 */
export function piezasPorPaqueteMerchandise(row) {
  if (gradoConsolidadoFromPackingRow(row) === 'RW') return '';
  return normPiezas(row?.piezas_por_paquete ?? row?.piezasPorPaquete);
}

/** Celda Pcs/paq en pantalla invoice (— si vacío o 0 en RW/NR). */
export function formatPcsPaqInvoiceCell(linea) {
  const v = linea?.piezasPorPaquete;
  if (v == null || String(v).trim() === '') return '—';
  const g = String(linea?.grado || '').trim().toUpperCase();
  if (String(v).trim() === '0' && (g === 'RW' || g === 'NR')) return '—';
  return v;
}

function merchandiseDimensionParts(row) {
  const text = `${safe(row?.productos_texto)} ${safe(row?.productName)} ${safe(row?.grado_celda)} ${safe(row?.grado)}`;
  const espesor = row?.espesor ?? row?.espessura;
  const ancho = row?.ancho ?? row?.largura;
  const largo = row?.largo ?? row?.comprimento;
  const esRw = /^\s*RW\s*$/i.test(safe(ancho)) || /\bRW\b/i.test(text);
  const dimensionKey = (value) => {
    const raw = safe(value).trim();
    if (!raw) return '';
    if (/^RW$/i.test(raw)) return 'RW';
    const parsed = parseDimNumber(raw);
    return parsed == null ? raw.toUpperCase() : String(parsed);
  };
  return [dimensionKey(espesor), esRw ? 'RW' : dimensionKey(ancho), dimensionKey(largo)];
}

/** Clave de configuración de packing logístico: piezas/paquete + producto + grado. */
export function productConfigKey(row) {
  const grado = gradoConsolidadoFromPackingRow(row);
  return `${normPiezas(row?.piezas_por_paquete)}|${productNameFromPackingRow(row).toUpperCase()}|${grado}`;
}

/** Clave comercial de PTC: producto + medida + grado + piezas/paquete. */
export function productMeasureGradeKey(row) {
  const grado = gradoConsolidadoFromPackingRow(row);
  const product = safe(row?.productName || productNameFromPackingRow(row))
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
  const piezas = piezasPorPaqueteMerchandise(row);
  return [product, ...merchandiseDimensionParts(row), grado, piezas].join('|');
}

/** Orden de grado en Invoice PTC: CD → RW → NR → resto. */
export function gradoPtcSortRank(grado) {
  const g = normGrado(grado);
  if (g === 'CD') return 0;
  if (g === 'RW') return 1;
  if (g === 'NR') return 2;
  return 50 + g.charCodeAt(0);
}

function measureSortKeyFromLinea(ln) {
  return [
    safe(ln?.espessura).trim(),
    safe(ln?.largura).trim().toUpperCase(),
    safe(ln?.comprimento).trim(),
  ].join('|');
}

function normMeasureFamilyPart(v) {
  const s = safe(v)
    .trim()
    .replace(/\bmm\b/gi, '')
    .replace(',', '.');
  if (!s) return '';
  const n = Number(s);
  if (Number.isFinite(n)) return String(n);
  return s.toUpperCase();
}

/**
 * Familia de medida (producto + espesor + largo): CD 1,8×1220×2134 y RW 1,8×RW×2134 comparten familia.
 * Orden: producto → familia → grado (CD, RW, NR) → medida completa → pcs/paq.
 */
export function merchandiseMeasureFamilyKey(ln) {
  return [
    normMeasureFamilyPart(ln?.espessura),
    normMeasureFamilyPart(ln?.comprimento),
  ].join('|');
}

/**
 * Orden PTC/PTB/prefactura: CD y RW de la misma familia quedan consecutivos (CD antes que RW).
 */
export function comparePtcMerchandiseLines(a, b) {
  const prod = safe(a?.productName).localeCompare(safe(b?.productName), 'es', {
    sensitivity: 'base',
  });
  if (prod !== 0) return prod;
  const fam = merchandiseMeasureFamilyKey(a).localeCompare(
    merchandiseMeasureFamilyKey(b),
    'es',
    { numeric: true }
  );
  if (fam !== 0) return fam;
  const gr = gradoPtcSortRank(a?.grado) - gradoPtcSortRank(b?.grado);
  if (gr !== 0) return gr;
  const meas = measureSortKeyFromLinea(a).localeCompare(measureSortKeyFromLinea(b), 'es', {
    numeric: true,
  });
  if (meas !== 0) return meas;
  return safe(a?.piezasPorPaquete).localeCompare(safe(b?.piezasPorPaquete), 'es');
}

export function sortPtcMerchandiseLines(lineas) {
  return [...(lineas || [])].sort(comparePtcMerchandiseLines);
}

/** Mismo criterio que PTC (formulario, PDF y Excel PTB). */
export const sortInvoiceMerchandiseLines = sortPtcMerchandiseLines;

/** PIs en el orden del packing list / invoice. Sin ids, usa el orden de la lista recibida. */
export function proformasOrdenadas(proformas, idProformas) {
  const lista = (proformas || []).filter(Boolean);
  const ids = idProformas || [];
  if (!ids.length) return lista;
  return ids
    .map((id) => lista.find((p) => String(p.id_proforma) === String(id)))
    .filter(Boolean);
}

function proformaLabelUpper(pi, clientes = []) {
  if (!pi) return '';
  return proformaContractLabel(pi, clientes).trim().toUpperCase();
}

/** Empareja nf_celda del PL comercial con una PI seleccionada. */
export function matchProformaByNfCelda(nfCelda, proformas, idProformas, clientes = []) {
  const label = safe(nfCelda).trim();
  if (!label) return null;
  const upper = label.toUpperCase().replace(/\s+/g, ' ');
  for (const pi of proformasOrdenadas(proformas, idProformas)) {
    const computed = proformaLabelUpper(pi, clientes).replace(/\s+/g, ' ');
    if (computed && computed === upper) return pi;
    const idPi = safe(pi.id_pi).trim().toUpperCase().replace(/\s+/g, ' ');
    if (idPi && idPi === upper) return pi;
    // Legado: snapshots antiguos guardaban "PI #id"
    if (`PI #${pi.id_proforma}`.toUpperCase() === upper) return pi;
  }
  return null;
}

/**
 * PI de precios para una fila del packing list.
 * Con nf_celda: debe coincidir con una PI seleccionada; sin nf_celda: primera PI con USD/m³ por medidas/grado.
 * Sin match → null (no hay fallback a la última PI).
 */
export function resolveProformaForPackingRow(
  row,
  proformas,
  idProformas,
  _fallbackProforma = null,
  calidades = [],
  clientes = []
) {
  const lista = proformasOrdenadas(proformas, idProformas);
  if (!lista.length) return null;

  const nfLabel = safe(row?.nf_celda).trim();
  if (nfLabel) {
    return matchProformaByNfCelda(row?.nf_celda, proformas, idProformas, clientes);
  }

  for (const pi of lista) {
    if (usdM3FromProformaForLine(row, pi, calidades) != null) return pi;
  }

  return null;
}

/** Filas del PL comercial con M³ > 0 que participan en precios PTC. */
function commercialRowsConM3(lineas) {
  return (lineas || []).filter((row) => {
    const m3 = Number(row?.total_m3);
    return Number.isFinite(m3) && m3 > 0;
  });
}

/** Diagnóstico: líneas sin PI o sin USD/m³ en la PI asignada. */
export function diagnosePtcPiPricing(lineas, proformas, idProformas, calidades = [], clientes = []) {
  const sinPi = [];
  const sinPrecio = [];

  for (const row of commercialRowsConM3(lineas)) {
    const nf = safe(row?.nf_celda).trim() || '—';
    const grado = gradoFromPackingRow(row) || '—';
    const dim = dimensionLineFromPackingRow(row) || '—';
    const pi = resolveProformaForPackingRow(
      row,
      proformas,
      idProformas,
      null,
      calidades,
      clientes
    );

    if (!pi) {
      sinPi.push({ nf, grado, dim });
      continue;
    }
    if (usdM3FromProformaForLine(row, pi, calidades) == null) {
      sinPrecio.push({
        nf,
        grado,
        dim,
        pi: proformaContractLabel(pi, clientes) || String(pi.id_proforma),
      });
    }
  }

  return {
    sinPi,
    sinPrecio,
    tieneErrores: sinPi.length > 0 || sinPrecio.length > 0,
  };
}

/** Mensaje de error para vista previa / guardado cuando falta PI o precio por línea. */
export function mensajePtcPiPricingError(
  lineas,
  proformas,
  idProformas,
  calidades = [],
  clientes = []
) {
  const d = diagnosePtcPiPricing(lineas, proformas, idProformas, calidades, clientes);
  if (!d.tieneErrores) return '';

  const lines = [
    'No se puede generar la Invoice PTC: hay líneas del packing list sin PI o sin precio.',
    '',
  ];

  if (d.sinPi.length) {
    lines.push('Sin PI asignada (revise columna NF / Contract del packing list):');
    for (const x of d.sinPi) {
      lines.push(`• NF «${x.nf}», grado ${x.grado}, medidas ${x.dim}`);
    }
    lines.push('');
  }

  if (d.sinPrecio.length) {
    lines.push('PI asignada pero sin USD/m³ para esa línea (complete la PI en Proformas):');
    for (const x of d.sinPrecio) {
      lines.push(`• NF «${x.nf}», grado ${x.grado}, medidas ${x.dim} → PI ${x.pi}`);
    }
  }

  return lines.join('\n').trim();
}

/**
 * Agrupa por producto, medida, grado y piezas/paquete.
 * Misma configuración con distinto USD/m³ (p. ej. otra PI) → otra línea.
 */
export function merchandiseGroupKey(row, _rowProforma = null, unitPrice = null) {
  const base = productMeasureGradeKey(row);
  const up =
    unitPrice != null && unitPrice !== '' && Number.isFinite(Number(unitPrice))
      ? round2(Number(unitPrice))
      : null;
  return up != null ? `${base}|$${up}` : `${base}|$`;
}

/** Conserva precios editados al regenerar líneas PTC desde el packing list. */
export function applyPtcFormUnitPrices(autoLineas, formLineas) {
  const form = Array.isArray(formLineas) ? formLineas : [];
  if (!form.length) return autoLineas || [];

  const byKey = new Map();
  for (const line of form) {
    const key = productMeasureGradeKey(line);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(line);
  }

  return (autoLineas || []).map((auto) => {
    const matches = byKey.get(productMeasureGradeKey(auto)) || [];
    if (!matches.length) return auto;

    let matchedM3 = 0;
    let matchedUsd = 0;
    let hasUnpricedLine = false;
    const unitPrices = [];
    for (const line of matches) {
      const quantity = Number(line?.quantityM3);
      if (!(Number.isFinite(quantity) && quantity > 0)) continue;
      const rawUnit = line?.unitPriceUsd ?? line?.unitPrice;
      const unit = rawUnit == null || rawUnit === '' ? null : Number(rawUnit);
      const rawTotal = line?.totalValueUsd ?? line?.totalValue;
      const total = rawTotal == null || rawTotal === '' ? null : Number(rawTotal);
      if (Number.isFinite(unit)) unitPrices.push(unit);
      if (Number.isFinite(total)) {
        matchedM3 += quantity;
        matchedUsd += total;
      } else if (Number.isFinite(unit)) {
        matchedM3 += quantity;
        matchedUsd += quantity * unit;
      } else {
        hasUnpricedLine = true;
      }
    }

    if (hasUnpricedLine) return auto;
    const unitPriceUsd = matchedM3 > 0
      ? round2(matchedUsd / matchedM3)
      : unitPrices.length
        ? round2(unitPrices[unitPrices.length - 1])
        : null;
    if (unitPriceUsd == null) return auto;

    const quantity = Number(auto?.quantityM3);
    const sameAsCalculated =
      auto.unitPriceUsd != null && Number(auto.unitPriceUsd) === unitPriceUsd;
    const totalValueUsd = sameAsCalculated
      ? auto.totalValueUsd
      : Number.isFinite(quantity) && quantity > 0
        ? round2(quantity * unitPriceUsd)
        : null;
    return { ...auto, unitPriceUsd, totalValueUsd };
  });
}

/** Número de medida: quita MM, espacios y comas de miles (1,220 → 1220). */
export function parseDimNumber(v) {
  let s = safe(v).replace(/\bmm\b/gi, '').trim();
  if (!s) return null;
  s = s.replace(/\s/g, '');
  while (/,(\d{3})(?=\d|,|$)/.test(s)) {
    s = s.replace(/,(\d{3})/g, '$1');
  }
  const n = parseFloat(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function isRwPackingRow(row) {
  return /\bRW\b/i.test(`${safe(row?.ancho)} ${safe(row?.productos_texto)} ${safe(row?.grado_celda)}`);
}

/** Tripleta esp x ancho x largo (ancho puede ser 'rw') para cruce con real_size de la PI. */
export function dimensionTripleFromPackingRow(row) {
  const esp = parseDimNumber(row?.espesor);
  const lar = parseDimNumber(row?.largo);
  if (esp == null || lar == null) return null;
  if (isRwPackingRow(row)) return [esp, 'rw', lar];
  const anc = parseDimNumber(row?.ancho);
  if (anc == null) return null;
  return [esp, anc, lar];
}

/** Parsea real_size guardado en la PI (Contract No. → productos / usd_m3). */
export function dimensionTripleFromPiRealSize(realSize) {
  const raw = safe(realSize).trim();
  if (!raw) return null;
  const parts = raw.split(/\s+x\s+/i).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  if (parts.length === 2) {
    const a = parseDimNumber(parts[0]);
    const b = parseDimNumber(parts[1]);
    if (a != null && b != null) return [a, b];
    return null;
  }
  const esp = parseDimNumber(parts[0]);
  const mid = parts[1];
  const lar = parseDimNumber(parts[parts.length - 1]);
  if (esp == null || lar == null) return null;
  if (/^rw$/i.test(mid)) return [esp, 'rw', lar];
  const anc = parseDimNumber(mid);
  if (anc == null) return null;
  return [esp, anc, lar];
}

export function triplesMatch(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (typeof x === 'string' || typeof y === 'string') {
      if (String(x).toLowerCase() !== String(y).toLowerCase()) return false;
    } else if (Math.abs(Number(x) - Number(y)) > 0.05) return false;
  }
  return true;
}

/** Cruce medidas PL ↔ PI; RW/NR en PL (ancho rw) vs PI (mismo esp/lar, calidad RW). */
function dimsMatchPackingToPi(plTriple, piTriple, plGrado, piCalLabel) {
  if (!plTriple || !piTriple) return false;
  if (triplesMatch(plTriple, piTriple)) return true;
  if (plTriple.length < 3 || piTriple.length < 3) return false;
  const espOk = Math.abs(Number(plTriple[0]) - Number(piTriple[0])) <= 0.05;
  const larOk = Math.abs(Number(plTriple[2]) - Number(piTriple[2])) <= 0.05;
  if (!espOk || !larOk) return false;
  const plGradoPi = plGrado === 'NR' ? 'RW' : plGrado;
  const piCalPi = piCalLabel === 'NR' ? 'RW' : piCalLabel;
  const plRw =
    String(plTriple[1]).toLowerCase() === 'rw' ||
    plGradoPi === 'RW' ||
    /\bRW\b/.test(plGradoPi) ||
    plGrado === 'NR';
  const piRw = String(piTriple[1]).toLowerCase() === 'rw' || piCalPi === 'RW';
  if (plRw && piRw) {
    if (plGradoPi && piCalPi) return plGradoPi === piCalPi;
    return true;
  }
  return false;
}

/**
 * Índice de línea de PI para una fila del packing list (grado + medidas).
 * Prioriza coincidencia dimensión + calidad (CD vs RW con mismo real_size).
 * Grado NR del PL se empareja con calidad RW de la PI.
 */
export function findPiIndexForPackingLine(row, proforma, calidades = []) {
  if (!proforma) return -1;
  const grado = gradoKey(gradoForPiPricing(row));
  const plTriple = dimensionTripleFromPackingRow(row);
  const len =Math.max(
    proforma.productos?.length || 0,
    proforma.real_size?.length || 0,
    proforma.calidad?.length || 0,
    proforma.usd_m3?.length || 0
  );
  if (!len) return -1;

  let dimMatch = -1;
  let gradoMatch = -1;

  for (let i = 0; i < len; i += 1) {
    const calLabel = gradoKey(calidadLabel(proforma.calidad?.[i], calidades));
    const piTriple = dimensionTripleFromPiRealSize(proforma.real_size?.[i]);
    const dimsOk = dimsMatchPackingToPi(plTriple, piTriple, grado, calLabel);

    if (dimsOk) {
      dimMatch = i;
      if (grado && calLabel && grado === calLabel) return i;
    }
    if (grado && calLabel === grado && gradoMatch < 0) gradoMatch = i;
  }

  if (dimMatch >= 0) return dimMatch;
  if (gradoMatch >= 0) return gradoMatch;

  for (let i = 0; i < (proforma.real_size?.length || 0); i += 1) {
    const piTriple = dimensionTripleFromPiRealSize(proforma.real_size[i]);
    if (dimsMatchPackingToPi(plTriple, piTriple, grado, '')) return i;
  }

  return len === 1 ? 0 : -1;
}

/** Clave texto para cruce secundario (PDF vs PI). */
export function packingLineRealSizeKey(row) {
  const dim = dimensionLineFromPackingRow(row);
  if (!dim) return '';
  return dim.replace(/\s+X\s+/gi, ' x ').replace(/\s+/g, ' ').trim();
}

function normRealSizeKey(s) {
  return safe(s)
    .toLowerCase()
    .replace(/\bmm\b/g, '')
    .replace(/(\d),(\d{3})/g, '$1$2')
    .replace(/\s+/g, ' ')
    .replace(/\s*x\s*/gi, ' x ')
    .trim();
}

function realSizeKeysForMatch(row) {
  const keys = new Set();
  const k = packingLineRealSizeKey(row);
  if (k) keys.add(normRealSizeKey(k));
  const triple = dimensionTripleFromPackingRow(row);
  if (triple) {
    keys.add(
      normRealSizeKey(
        triple.map((p) => (typeof p === 'number' ? String(p) : p)).join(' x ')
      )
    );
  }
  return [...keys];
}

/** PI u OC: mismos campos de precio (Contract No.). */
export function normalizePricingSource(src) {
  if (!src) return null;
  return {
    real_size: src.real_size,
    usd_m3: src.usd_m3,
    m3: src.m3,
    usd_total: src.usd_total,
    valor_proforma: src.valor_proforma ?? src.valor_ordencompra ?? null,
  };
}

function unitUsdM3AtProformaIndex(proforma, index) {
  const p = proforma?.usd_m3?.[index];
  if (p != null && Number.isFinite(Number(p)) && Number(p) > 0) {
    return Number(p);
  }
  const m3 = Number(proforma?.m3?.[index]);
  const total = Number(proforma?.usd_total?.[index]);
  if (m3 > 0 && total > 0) return round2(total / m3);
  return null;
}

function uniquePositiveUsdM3(proforma) {
  const priced = (proforma?.usd_m3 || [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(priced.map((n) => round2(n)))];
}

/** Promedio ponderado USD/m³ desde líneas de la PI (usd_m3, usd_total o valor total). */
export function usdM3AverageFromProforma(proforma) {
  const src = normalizePricingSource(proforma);
  if (!src) return null;
  const m3arr = src.m3 || [];
  let sumM3 = 0;
  let sumUsd = 0;
  for (let i = 0; i < m3arr.length; i += 1) {
    const m = Number(m3arr[i]);
    if (!(m > 0)) continue;
    sumM3 += m;
    const unit = unitUsdM3AtProformaIndex(src, i);
    if (unit != null) sumUsd += m * unit;
    else {
      const t = Number(src.usd_total?.[i]);
      if (t > 0) sumUsd += t;
    }
  }
  if (sumM3 > 0 && sumUsd > 0) return round2(sumUsd / sumM3);
  const valor = Number(src.valor_proforma);
  if (sumM3 > 0 && valor > 0) return round2(valor / sumM3);
  return null;
}

/** Precio unitario por defecto cuando todas las líneas comparten el mismo USD/m³ o un promedio único. */
export function usdM3DefaultFromProforma(proforma) {
  const src = normalizePricingSource(proforma);
  if (!src) return null;
  const unique = uniquePositiveUsdM3(src);
  if (unique.length === 1) return unique[0];
  return usdM3AverageFromProforma(src);
}

/**
 * Precio unitario USD/m³ de la PI (Contract No.) para una línea del packing list.
 * Cruza grado (CD/RW; NR ≡ RW) + medidas; UNIT PRICE del PDF = este valor.
 */
export function usdM3FromProformaForLine(row, proforma, calidades = []) {
  const pi = normalizePricingSource(proforma);
  if (!pi) return null;

  const idx = findPiIndexForPackingLine(row, proforma, calidades);
  if (idx >= 0) {
    const p = unitUsdM3AtProformaIndex(pi, idx);
    if (p != null) return p;
  }

  if (!pi.real_size?.length) {
    const unique = uniquePositiveUsdM3(pi);
    return unique.length === 1 ? unique[0] : null;
  }

  const plTriple = dimensionTripleFromPackingRow(row);
  const grado = gradoKey(gradoForPiPricing(row));

  for (let i = 0; i < pi.real_size.length; i += 1) {
    const piTriple = dimensionTripleFromPiRealSize(pi.real_size[i]);
    const calLabel = gradoKey(calidadLabel(proforma?.calidad?.[i], calidades));
    if (dimsMatchPackingToPi(plTriple, piTriple, grado, calLabel)) {
      const p = unitUsdM3AtProformaIndex(pi, i);
      if (p != null) return p;
    }
  }

  const keys = realSizeKeysForMatch(row);
  for (let i = 0; i < pi.real_size.length; i += 1) {
    const piKey = normRealSizeKey(pi.real_size[i]);
    if (keys.some((k) => k === piKey)) {
      const p = unitUsdM3AtProformaIndex(pi, i);
      if (p != null) return p;
    }
  }

  const unique = uniquePositiveUsdM3(pi);
  if (unique.length === 1) return unique[0];
  return null;
}

export function round3(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 1000) / 1000;
}

export function round2(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Math.round(Number(n) * 100) / 100;
}

const ONES = [
  '',
  'ONE',
  'TWO',
  'THREE',
  'FOUR',
  'FIVE',
  'SIX',
  'SEVEN',
  'EIGHT',
  'NINE',
  'TEN',
  'ELEVEN',
  'TWELVE',
  'THIRTEEN',
  'FOURTEEN',
  'FIFTEEN',
  'SIXTEEN',
  'SEVENTEEN',
  'EIGHTEEN',
  'NINETEEN',
];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

function numberToWordsEnUnder1000(n) {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const u = n % 10;
    return u ? `${TENS[t]} ${ONES[u]}` : TENS[t];
  }
  const h = Math.floor(n / 100);
  const r = n % 100;
  return r ? `${ONES[h]} HUNDRED ${numberToWordsEnUnder1000(r)}` : `${ONES[h]} HUNDRED`;
}

function numberToWordsEnInt(n) {
  if (n === 0) return 'ZERO';
  let s = '';
  const billions = Math.floor(n / 1e9);
  n %= 1e9;
  const millions = Math.floor(n / 1e6);
  n %= 1e6;
  const thousands = Math.floor(n / 1000);
  const rest = n % 1000;
  if (billions) s += `${numberToWordsEnUnder1000(billions)} BILLION `;
  if (millions) s += `${numberToWordsEnUnder1000(millions)} MILLION `;
  if (thousands) s += `${numberToWordsEnUnder1000(thousands)} THOUSAND `;
  if (rest) s += numberToWordsEnUnder1000(rest);
  return s.trim().replace(/\s+/g, ' ');
}

/** Monto USD en letras (inglés) para TOTAL DUE. */
export function usdAmountInWords(amount) {
  const n = round2(amount);
  if (!n) return 'ZERO DOLLARS';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  let s = `${numberToWordsEnInt(whole)} DOLLAR${whole === 1 ? '' : 'S'}`;
  if (cents) {
    s += ` AND ${numberToWordsEnInt(cents)} CENT${cents === 1 ? '' : 'S'}`;
  }
  return s;
}

function categoriaNombreFromPackingRow(row, proforma, opts = {}) {
  const { productos = [], categorias = [], calidades = [] } = opts;
  const idx = findPiIndexForPackingLine(row, proforma, calidades);
  if (idx >= 0 && proforma?.productos?.[idx] != null) {
    const prod = productos.find(
      (p) => String(p.id_producto) === String(proforma.productos[idx])
    );
    const cat = categorias.find((c) => String(c.id_categoria) === String(prod?.categoria));
    if (cat?.nombre) return String(cat.nombre).trim();
    if (cat?.nombre_ingles) return String(cat.nombre_ingles).trim();
  }
  return matchCategoriaEnTextoFromOpts(row?.productos_texto, categorias);
}

function matchCategoriaEnTextoFromOpts(texto, categorias) {
  const txt = safe(texto).trim();
  if (!txt || !categorias?.length) return '';
  const upper = txt.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
  const cats = [...categorias]
    .map((cat) => ({
      nombre: safe(cat?.nombre).trim(),
      ingles: safe(cat?.nombre_ingles).trim(),
    }))
    .filter((cat) => cat.nombre || cat.ingles)
    .sort((a, b) => {
      const la = (b.ingles || b.nombre).length;
      const lb = (a.ingles || a.nombre).length;
      return la - lb;
    });
  for (const cat of cats) {
    const en = cat.ingles.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
    const es = cat.nombre.normalize('NFD').replace(/\p{M}/gu, '').toUpperCase();
    if (en && upper.includes(en)) return cat.nombre || cat.ingles;
    if (es && upper.includes(es)) return cat.nombre || cat.ingles;
  }
  return '';
}

/** Etiqueta PTB (categoría en español plural) para una línea del packing list. */
export function etiquetaPtbForPackingRow(row, proforma, opts = {}) {
  const fromCat = categoriaNombreFromPackingRow(row, proforma, opts);
  if (fromCat) return etiquetaPtbCategoriaEspanolPlural(fromCat);
  return etiquetaPtbFromDescription(
    opts.productDescriptionSnapshot,
    productNameFromPackingRow(row),
    opts.categorias
  );
}

/**
 * Agrupa líneas del packing list y calcula filas de mercancía para invoice/PDF.
 * @param {Array} lineas - detalle packing list (API o Excel)
 * @param {object|null} proforma - PI fallback (compat. PTB / una sola PI)
 * @param {object} opts - proformasLista, idProformas, calidades, currency, …
 */
export function buildInvoicePtcMerchandise(lineas, proforma, opts = {}) {
  const calidades = opts.calidades || [];
  const clientes = opts.clientes || [];
  const currency = safe(opts.currency || 'USD').trim().toUpperCase() || 'USD';
  const proformasLista =
    opts.proformasLista !== undefined
      ? opts.proformasLista
      : proforma
        ? [proforma]
        : [];
  const idProformas =
    opts.idProformas !== undefined
      ? opts.idProformas
      : proforma?.id_proforma != null
        ? [proforma.id_proforma]
        : [];
  const pisActivas = proformasOrdenadas(proformasLista, idProformas);
  const pricingEstricto = pisActivas.length > 0;
  const groups = new Map();

  for (const row of lineas || []) {
    const m3 = Number(row?.total_m3);
    if (!Number.isFinite(m3) || m3 <= 0) continue;

    let rowProforma = null;
    let up = null;
    if (pricingEstricto) {
      rowProforma = resolveProformaForPackingRow(
        row,
        proformasLista,
        idProformas,
        null,
        calidades,
        clientes
      );
      if (!rowProforma) continue;
      // Si la PI no trae USD/m³ para el grado (p. ej. RW), igual incluimos la línea
      // para que el usuario pueda completar el unitario en el formulario.
      up = usdM3FromProformaForLine(row, rowProforma, calidades);
    } else {
      rowProforma = proforma || null;
      up = rowProforma ? usdM3FromProformaForLine(row, rowProforma, calidades) : null;
    }
    const key = merchandiseGroupKey(row, rowProforma, up);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        piezasPorPaquete: piezasPorPaqueteMerchandise(row),
        productName: productNameFromPackingRow(row),
        grado: gradoConsolidadoFromPackingRow(row),
        sumM3: 0,
        sumBundles: 0,
        sumPiezas: 0,
        sumUsd: 0,
        hasMissingPrice: false,
        proforma: rowProforma,
        sampleRow: row,
      };
      groups.set(key, g);
    }
    g.sumM3 += m3;
    const bundles = Number(row?.cantidad_paquetes);
    if (Number.isFinite(bundles)) g.sumBundles += bundles;
    const pcs = Number(row?.total_piezas);
    if (Number.isFinite(pcs)) g.sumPiezas += pcs;
    if (up != null && Number.isFinite(Number(up))) {
      g.sumUsd += m3 * Number(up);
    } else {
      g.hasMissingPrice = true;
    }
  }

  const lineasMercancia = [];
  let totalM3 = 0;
  let totalBundles = 0;
  let totalUsd = 0;

  for (const g of groups.values()) {
    const quantityM3 = round3(g.sumM3);
    // Si las PI aportan tarifas distintas, el unitario representa el promedio ponderado.
    // Si falta una tarifa dentro del grupo, se deja pendiente para que el usuario la complete.
    const tienePrecioCompleto = !g.hasMissingPrice && quantityM3 > 0;
    const unitPriceUsd = tienePrecioCompleto
      ? round2(g.sumUsd / g.sumM3)
      : null;
    const totalValueUsd = tienePrecioCompleto ? round2(g.sumUsd) : null;

    totalM3 += quantityM3;
    totalBundles += g.sumBundles;
    if (totalValueUsd != null) totalUsd += totalValueUsd;

    const ptbDims = ptbDimsFromPackingRow(g.sampleRow);
    lineasMercancia.push({
      quantityM3,
      productName: g.productName,
      grado: g.grado || '',
      internalCode: internalCodeFromGroup(g),
      descriptionBlock: merchandiseDescriptionBlock(
        g,
        opts.productDescriptionSnapshot
      ),
      etiquetaPtb: etiquetaPtbForPackingRow(g.sampleRow, g.proforma || proforma, {
        ...opts,
        productName: g.productName,
      }),
      espessura: ptbDims.espessura,
      largura: ptbDims.largura,
      comprimento: ptbDims.comprimento,
      piezasPorPaquete: g.piezasPorPaquete,
      bundles: round3(g.sumBundles),
      totalPiezas: round3(g.sumPiezas),
      unitPriceUsd,
      totalValueUsd,
      currency,
      djcNum: safe(opts.numeroInvoice),
      djcDate: opts.fechaInvoice || opts.fechaEmbarque || '',
    });
  }

  const lineasOrdenadas = sortPtcMerchandiseLines(lineasMercancia);

  return {
    lineasMercancia: normalizeGeneralMerchandiseDescription(
      lineasOrdenadas,
      opts.productDescriptionSnapshot
    ),
    totalM3: round3(totalM3),
    totalBundles: round3(totalBundles),
    totalUsd: round2(totalUsd),
    currency,
  };
}

/** Totales de paquetes y M³ sobre todas las líneas (incl. sin m3). */
export function packingListTotals(lineas) {
  let totalBundles = 0;
  let totalM3 = 0;
  for (const row of lineas || []) {
    const b = Number(row?.cantidad_paquetes);
    if (Number.isFinite(b)) totalBundles += b;
    const m = Number(row?.total_m3);
    if (Number.isFinite(m)) totalM3 += m;
  }
  return { totalBundles: round3(totalBundles), totalM3: round3(totalM3) };
}
